import React, { createContext, useContext, useState, useCallback } from 'react';
import { translations } from '../i18n/translations';
import { getDefaultLanguage } from '../utils/defaultLanguage';

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || getDefaultLanguage();
  });

  const changeLanguage = useCallback((lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
  }, []);

  const toggleLanguage = useCallback(() => {
    // Cycle through supported languages: en -> ru -> zh -> tr -> ar -> en
    const langOrder = ['en', 'ru', 'zh', 'tr', 'ar'];
    const currentIndex = langOrder.indexOf(language);
    const nextIndex = (currentIndex + 1) % langOrder.length;
    changeLanguage(langOrder[nextIndex]);
  }, [language, changeLanguage]);

  // Translation function with nested key support and interpolation
  const t = useCallback(
    (key, params = {}) => {
      const keys = key.split('.');
      let value = translations[language];
      
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          // Fallback to English if key not found
          let fallback = translations['en'];
          for (const fk of keys) {
            if (fallback && typeof fallback === 'object' && fk in fallback) {
              fallback = fallback[fk];
            } else {
              return key; // Return key if not found in fallback either
            }
          }
          value = fallback;
          break;
        }
      }
      
      // Handle interpolation: replace {{key}} with params[key]
      if (typeof value === 'string' && Object.keys(params).length > 0) {
        Object.entries(params).forEach(([paramKey, paramValue]) => {
          value = value.replace(new RegExp(`{{${paramKey}}}`, 'g'), paramValue);
        });
      }
      
      return value;
    },
    [language]
  );

  const value = {
    language,
    setLanguage: changeLanguage,
    toggleLanguage,
    t,
    isRussian: language === 'ru',
    isEnglish: language === 'en',
    isChinese: language === 'zh',
    isTurkish: language === 'tr',
    isArabic: language === 'ar',
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;

